import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import { ContentMasterService } from '../../api/generated/services/ContentMasterService';
import type { ContentMasterFilterFieldListView } from '../../api/generated/models/ContentMasterFilterFieldListView';
import type { MarketplaceCategoryListView } from '../../api/generated/models/MarketplaceCategoryListView';
import { CancelError, type CancelablePromise } from '../../api/generated/core/CancelablePromise';
import MarketplaceCategoryPicker from './MarketplaceCategoryPicker';
import {
  CONTENT_MASTER_FIELD_FILTERS_MAX,
  CONTENT_MASTER_FIELD_FILTER_VALUE_MAX,
  type ContentMasterFieldFilter,
} from '../../store/slices/contentMasterSlice';

/** Debounce for the field-label search inside an open field dropdown. */
const FIELD_SEARCH_DEBOUNCE_MS = 400;
/** `limit` per group for GET /content-master/filter-fields (default 50, max 200). */
const FILTER_FIELDS_LIMIT = 50;
/** Page size for the non-SCX category picker (`GET /content-master/categories`). */
const CATEGORY_PAGE_LIMIT = 50;
/** Same page sizes the toolbar select used to offer. */
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/** Everything the modal edits, committed in ONE Apply click. */
export interface ContentMasterFilterModalResult {
  kCategory: number | null;
  kCategoryName: string | null;
  /** Marketplace channels only (native Wawi category next to the SCX one); `null` elsewhere. */
  kWawiCategory: number | null;
  kWawiCategoryName: string | null;
  /** Include products of ALL descendant categories (only meaningful with a category). */
  includeSubcategories: boolean;
  fieldFilters: ContentMasterFieldFilter[];
  /** Wawi flags - marketplace channels only; echoed back unchanged for other channels. */
  onlyInStock: boolean;
  onlyWithEan: boolean;
  onlyNotActivated: boolean;
  active: boolean;
  limit: number;
  /** Marketplace channels only; echoed back unchanged for other channels. */
  includeUnassigned: boolean;
}

export interface ContentMasterFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Channel identifier `plattform:N` / `shop:N`. */
  source: string;
  dataSourceId?: string | null;
  /** List language filter ('' = shop default) - forwarded to the reference routes. */
  language: string;
  /**
   * SCX marketplace channel: categories come from
   * `GET /marketplace-listing/categories`, `mpAttr_*` fields are available and
   * the "All" (includeUnassigned) toggle is rendered.
   * Other channels use `GET /content-master/categories` (native Wawi tree).
   */
  sourceIsMarketplace: boolean;
  /** Currently committed values - the drafts reseed from them on every open. */
  kCategory: number | null;
  kCategoryName: string | null;
  kWawiCategory: number | null;
  kWawiCategoryName: string | null;
  includeSubcategories: boolean;
  fieldFilters: ContentMasterFieldFilter[];
  onlyInStock: boolean;
  onlyWithEan: boolean;
  onlyNotActivated: boolean;
  active: boolean;
  limit: number;
  includeUnassigned: boolean;
  /** Apply click: commit the drafts (ONE list refetch) and close the modal. */
  onApply: (result: ContentMasterFilterModalResult) => void;
}

/** One editable "field → phrase" row. Incomplete rows are dropped on Apply. */
interface FieldFilterRow {
  field: string;
  value: string;
}

type FieldGroupKey = 'coreFields' | 'wawiAttributes' | 'merkmale' | 'mpAttributes';

const FIELD_GROUPS: Array<{ key: FieldGroupKey; labelKey: string }> = [
  { key: 'coreFields', labelKey: 'content_master_filter_group_core' },
  { key: 'wawiAttributes', labelKey: 'content_master_filter_group_wawi' },
  { key: 'merkmale', labelKey: 'content_master_filter_group_merkmale' },
  { key: 'mpAttributes', labelKey: 'content_master_filter_group_mp' },
];

/**
 * Search-filter modal of the Content Master products list. Holds EVERY list
 * filter except the toolbar basics (free-text search, status tabs, channel
 * select, pagination). Layout: the "Active" / "All" toggles sit first (top
 * left, hover tooltips) with the page-size select in the top-right corner;
 * below them the category filter (exact match or - via the
 * "Including subcategories" checkbox - the whole subtree) and up to 10
 * "field → phrase" filters (AND-combined contains-match, field keys from
 * `GET /content-master/filter-fields`), each group in its own bordered
 * section.
 *
 * Everything is DRAFT state - nothing is dispatched (and the list is not
 * refetched) until the user presses Apply. Cancel / close discards the
 * drafts; they reseed from the committed filters on the next open.
 */
const ContentMasterFilterModal: React.FC<ContentMasterFilterModalProps> = ({
  isOpen,
  onClose,
  source,
  dataSourceId,
  language,
  sourceIsMarketplace,
  kCategory,
  kCategoryName,
  kWawiCategory,
  kWawiCategoryName,
  includeSubcategories,
  fieldFilters,
  onlyInStock,
  onlyWithEan,
  onlyNotActivated,
  active,
  limit,
  includeUnassigned,
  onApply,
}) => {
  const { t } = useTranslation();

  // ---- Draft state (reseeded from the committed filters on every open) ----

  const [draftKCategory, setDraftKCategory] = useState<number | null>(kCategory);
  const [draftKCategoryName, setDraftKCategoryName] = useState<string | null>(kCategoryName);
  const [draftKWawiCategory, setDraftKWawiCategory] = useState<number | null>(kWawiCategory);
  const [draftKWawiCategoryName, setDraftKWawiCategoryName] = useState<string | null>(
    kWawiCategoryName
  );
  const [draftIncludeSubcategories, setDraftIncludeSubcategories] =
    useState(includeSubcategories);
  const [rows, setRows] = useState<FieldFilterRow[]>(() => fieldFilters.map((f) => ({ ...f })));
  const [draftOnlyInStock, setDraftOnlyInStock] = useState(onlyInStock);
  const [draftOnlyWithEan, setDraftOnlyWithEan] = useState(onlyWithEan);
  const [draftOnlyNotActivated, setDraftOnlyNotActivated] = useState(onlyNotActivated);
  const [draftActive, setDraftActive] = useState(active);
  const [draftLimit, setDraftLimit] = useState(limit);
  const [draftIncludeUnassigned, setDraftIncludeUnassigned] = useState(includeUnassigned);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  /** Marketplace channels only: the second (Wawi) category picker. */
  const [wawiCategoryPickerOpen, setWawiCategoryPickerOpen] = useState(false);
  /** Index of the row whose field dropdown is open (`null` = all closed). */
  const [openRowIndex, setOpenRowIndex] = useState<number | null>(null);
  const [fieldSearch, setFieldSearch] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    // Discard whatever a previously cancelled visit left behind.
    setDraftKCategory(kCategory);
    setDraftKCategoryName(kCategoryName);
    setDraftKWawiCategory(kWawiCategory);
    setDraftKWawiCategoryName(kWawiCategoryName);
    setDraftIncludeSubcategories(includeSubcategories);
    setRows(fieldFilters.map((f) => ({ ...f })));
    setDraftOnlyInStock(onlyInStock);
    setDraftOnlyWithEan(onlyWithEan);
    setDraftOnlyNotActivated(onlyNotActivated);
    setDraftActive(active);
    setDraftLimit(limit);
    setDraftIncludeUnassigned(includeUnassigned);
    setCategoryPickerOpen(false);
    setWawiCategoryPickerOpen(false);
    setOpenRowIndex(null);
    setFieldSearch('');
    // Reseed ONLY when the modal opens - the committed values cannot change
    // while it is open (the page behind it is inert).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const anyCategorySelected = draftKCategory != null || draftKWawiCategory != null;

  const handleApply = () => {
    onApply({
      kCategory: draftKCategory,
      kCategoryName: draftKCategoryName,
      // The Wawi picker only exists on marketplace channels.
      kWawiCategory: sourceIsMarketplace ? draftKWawiCategory : null,
      kWawiCategoryName: sourceIsMarketplace ? draftKWawiCategoryName : null,
      // Meaningless without a category - never commit it as `true` alone.
      includeSubcategories: anyCategorySelected && draftIncludeSubcategories,
      fieldFilters: rows
        .filter((r) => r.field !== '' && r.value.trim() !== '')
        .map((r) => ({ field: r.field, value: r.value })),
      onlyInStock: draftOnlyInStock,
      onlyWithEan: draftOnlyWithEan,
      onlyNotActivated: draftOnlyNotActivated,
      active: draftActive,
      limit: draftLimit,
      includeUnassigned: draftIncludeUnassigned,
    });
  };

  // ---- Field dropdown (per-row combobox with server-side label search) ----

  const [fieldGroups, setFieldGroups] = useState<ContentMasterFilterFieldListView | null>(null);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [fieldsError, setFieldsError] = useState(false);
  const fieldsRequestRef = useRef<CancelablePromise<ContentMasterFilterFieldListView> | null>(
    null
  );
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  /**
   * field key → label cache, merged from every /filter-fields response. Used
   * to render the trigger label of rows restored from the committed filters
   * (e.g. after a URL restore) whose label we have seen at least once; raw
   * key is the fallback.
   */
  const labelMapRef = useRef<Record<string, string>>({});
  /** Query key of the list currently in `fieldGroups`; reopening with the same key reuses it. */
  const loadedFieldsKeyRef = useRef<string | null>(null);

  const dropdownOpen = openRowIndex != null;

  useEffect(() => {
    if (!dropdownOpen || !source) {
      return;
    }
    const term = fieldSearch.trim();
    const categoryParam =
      sourceIsMarketplace && draftKCategory != null ? draftKCategory : undefined;
    const key = JSON.stringify([source, dataSourceId, language, term, categoryParam]);
    if (key === loadedFieldsKeyRef.current) {
      // Drop a still-running request for another term so it cannot overwrite this list.
      fieldsRequestRef.current?.cancel();
      setFieldsLoading(false);
      setFieldsError(false);
      return;
    }
    setFieldsLoading(true);
    setFieldsError(false);
    const timer = setTimeout(
      () => {
        fieldsRequestRef.current?.cancel();
        const req = ContentMasterService.getAppContentMasterFilterFields(
          source,
          dataSourceId?.trim() || undefined,
          language.trim() || undefined,
          term || undefined,
          // SCX marketplaces only: include the DRAFT category's listing attributes.
          categoryParam,
          FILTER_FIELDS_LIMIT
        );
        fieldsRequestRef.current = req;
        req
          .then((res) => {
            for (const group of FIELD_GROUPS) {
              for (const item of res[group.key] ?? []) {
                if (item.field && item.label) {
                  labelMapRef.current[item.field] = item.label;
                }
              }
            }
            loadedFieldsKeyRef.current = key;
            setFieldGroups(res);
            setFieldsLoading(false);
          })
          .catch((e) => {
            if (e instanceof CancelError) {
              return;
            }
            loadedFieldsKeyRef.current = null;
            setFieldGroups(null);
            setFieldsError(true);
            setFieldsLoading(false);
          });
      },
      term ? FIELD_SEARCH_DEBOUNCE_MS : 0
    );
    return () => {
      clearTimeout(timer);
    };
  }, [dropdownOpen, fieldSearch, source, dataSourceId, language, sourceIsMarketplace, draftKCategory]);

  // Close the open dropdown on any outside click.
  useEffect(() => {
    if (!dropdownOpen) {
      return;
    }
    const handleMouseDown = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenRowIndex(null);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [dropdownOpen]);

  const fieldLabel = (field: string): string => labelMapRef.current[field] ?? field;

  const toggleDropdown = (index: number) => {
    setOpenRowIndex((prev) => (prev === index ? null : index));
  };

  const selectField = (index: number, field: string) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, field } : row)));
    setOpenRowIndex(null);
  };

  const updateRowValue = (index: number, value: string) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, value } : row)));
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setOpenRowIndex(null);
  };

  const addRow = () => {
    setRows((prev) =>
      prev.length >= CONTENT_MASTER_FIELD_FILTERS_MAX
        ? prev
        : [...prev, { field: '', value: '' }]
    );
  };

  // ---- Category picker ----------------------------------------------------

  /**
   * Non-SCX channels: native Wawi category tree from
   * `GET /content-master/categories` - same q / parentKCategory / offset
   * contract as the SCX route, so the SCX picker component is reused as-is.
   * (`ContentMasterWawiCategoryListView` is a structural subset of
   * `MarketplaceCategoryListView`.)
   */
  const fetchWawiCategories = useCallback(
    (opts: {
      q?: string;
      parentKCategory?: number;
      offset?: number;
    }): CancelablePromise<MarketplaceCategoryListView> =>
      ContentMasterService.getAppContentMasterCategories(
        source,
        dataSourceId?.trim() || undefined,
        language.trim() || undefined,
        opts.q,
        opts.parentKCategory,
        CATEGORY_PAGE_LIMIT,
        opts.offset
      ),
    [source, dataSourceId, language]
  );

  const handleCategorySelect = (selectedKCategory: number, categoryName: string) => {
    setCategoryPickerOpen(false);
    setDraftKCategory(selectedKCategory);
    setDraftKCategoryName(categoryName);
  };

  const handleCategoryClear = () => {
    setDraftKCategory(null);
    setDraftKCategoryName(null);
    // The subtree toggle is bound to the categories - clearing the last one clears it too.
    if (draftKWawiCategory == null) {
      setDraftIncludeSubcategories(false);
    }
  };

  const handleWawiCategorySelect = (selectedKCategory: number, categoryName: string) => {
    setWawiCategoryPickerOpen(false);
    setDraftKWawiCategory(selectedKCategory);
    setDraftKWawiCategoryName(categoryName);
  };

  const handleWawiCategoryClear = () => {
    setDraftKWawiCategory(null);
    setDraftKWawiCategoryName(null);
    if (draftKCategory == null) {
      setDraftIncludeSubcategories(false);
    }
  };

  // ---- Render -------------------------------------------------------------

  const groupsToRender = FIELD_GROUPS.map((group) => {
    const list = (fieldGroups ? fieldGroups[group.key] : undefined) ?? [];
    const items: Array<{ field: string; label: string }> = [];
    for (const item of list) {
      if (typeof item.field === 'string' && item.field !== '') {
        items.push({ field: item.field, label: item.label || item.field });
      }
    }
    return { ...group, items };
  }).filter((group) => group.items.length > 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('content_master_filter_panel_title')}
      isCentered
      hideCloseButton
      className="content-master-filter-modal"
    >
      <div className="content-master-filter-modal__body">
        {/* Top row: Active / All toggles (hover tooltips) + page size on the right. */}
        <div className="content-master-filter-modal__section content-master-filter-modal__top">
          <div className="content-master-filter-modal__top-checks">
            <label className="content-master-active-filter">
              <input
                type="checkbox"
                className="content-master-checkbox"
                checked={draftActive}
                onChange={(e) => setDraftActive(e.target.checked)}
              />
              <span className="content-master-active-filter__label">
                {t('content_master_active_filter_label')}
              </span>
              <span
                className="content-master-other-sources__tooltip content-master-active-filter__tooltip"
                role="tooltip"
              >
                {t('content_master_active_filter_tooltip')}
              </span>
            </label>
            {sourceIsMarketplace ? (
              <>
                <label className="content-master-active-filter">
                  <input
                    type="checkbox"
                    className="content-master-checkbox"
                    checked={draftIncludeUnassigned}
                    onChange={(e) => setDraftIncludeUnassigned(e.target.checked)}
                  />
                  <span className="content-master-active-filter__label">
                    {t('cm_include_unassigned_label')}
                  </span>
                  <span
                    className="content-master-other-sources__tooltip content-master-active-filter__tooltip"
                    role="tooltip"
                  >
                    {t('cm_include_unassigned_tooltip')}
                  </span>
                </label>
                <label className="content-master-active-filter">
                  <input
                    type="checkbox"
                    className="content-master-checkbox"
                    checked={draftOnlyInStock}
                    onChange={(e) => setDraftOnlyInStock(e.target.checked)}
                  />
                  <span className="content-master-active-filter__label">
                    {t('cm_only_in_stock_label')}
                  </span>
                  <span
                    className="content-master-other-sources__tooltip content-master-active-filter__tooltip"
                    role="tooltip"
                  >
                    {t('cm_only_in_stock_tooltip')}
                  </span>
                </label>
                <label className="content-master-active-filter">
                  <input
                    type="checkbox"
                    className="content-master-checkbox"
                    checked={draftOnlyWithEan}
                    onChange={(e) => setDraftOnlyWithEan(e.target.checked)}
                  />
                  <span className="content-master-active-filter__label">
                    {t('cm_only_with_ean_label')}
                  </span>
                  <span
                    className="content-master-other-sources__tooltip content-master-active-filter__tooltip"
                    role="tooltip"
                  >
                    {t('cm_only_with_ean_tooltip')}
                  </span>
                </label>
                <label className="content-master-active-filter">
                  <input
                    type="checkbox"
                    className="content-master-checkbox"
                    checked={draftOnlyNotActivated}
                    onChange={(e) => setDraftOnlyNotActivated(e.target.checked)}
                  />
                  <span className="content-master-active-filter__label">
                    {t('cm_only_not_activated_label')}
                  </span>
                  <span
                    className="content-master-other-sources__tooltip content-master-active-filter__tooltip"
                    role="tooltip"
                  >
                    {t('cm_only_not_activated_tooltip')}
                  </span>
                </label>
              </>
            ) : null}
          </div>
          <label className="content-master-filter-modal__per-page">
            <span>{t('content_master_products_per_page_label')}</span>
            <select
              className="content-master-toolbar-select content-master-toolbar-select--narrow"
              value={String(draftLimit)}
              onChange={(e) => setDraftLimit(Number(e.target.value))}
              aria-label={t('content_master_products_per_page_aria')}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={String(n)}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          {sourceIsMarketplace && draftOnlyNotActivated && !draftIncludeUnassigned ? (
            <div className="content-master-filter-modal__hint">
              {t('cm_only_not_activated_unassigned_hint')}
            </div>
          ) : null}
        </div>

        <div className="content-master-filter-modal__section">
          <span className="content-master-filter-modal__label">
            {t(
              sourceIsMarketplace
                ? 'content_master_filter_mp_category_label'
                : 'content_master_filter_category_label'
            )}
          </span>
          {draftKCategory != null ? (
            <span className="content-master-filter-modal__category-chip">
              <span className="content-master-filter-modal__category-name">
                {draftKCategoryName || `#${draftKCategory}`}
              </span>
              <button
                type="button"
                className="content-master-filter-modal__category-clear"
                onClick={handleCategoryClear}
                aria-label={t('content_master_filter_category_clear')}
                title={t('content_master_filter_category_clear')}
              >
                ×
              </button>
            </span>
          ) : categoryPickerOpen ? (
            <div className="content-master-filter-modal__category-picker">
              <MarketplaceCategoryPicker
                source={source}
                dataSourceId={dataSourceId}
                mode="select"
                onSelect={handleCategorySelect}
                fetchPage={sourceIsMarketplace ? undefined : fetchWawiCategories}
              />
              <button
                type="button"
                className="content-master-filter-modal__category-cancel"
                onClick={() => setCategoryPickerOpen(false)}
              >
                {t('content_master_bulk_modal_cancel')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="content-master-filter-modal__category-choose"
              onClick={() => setCategoryPickerOpen(true)}
            >
              {t('content_master_filter_category_choose')}
            </button>
          )}
          {sourceIsMarketplace ? (
            <>
              {/* Marketplace channels: second, independent picker over the
                  native Wawi tree (`kWawiCategory`, AND-combined with the SCX
                  category above). Same picker component, same route the
                  non-SCX channels use. */}
              <span className="content-master-filter-modal__label">
                {t('content_master_filter_wawi_category_label')}
              </span>
              {draftKWawiCategory != null ? (
                <span className="content-master-filter-modal__category-chip">
                  <span className="content-master-filter-modal__category-name">
                    {draftKWawiCategoryName || `#${draftKWawiCategory}`}
                  </span>
                  <button
                    type="button"
                    className="content-master-filter-modal__category-clear"
                    onClick={handleWawiCategoryClear}
                    aria-label={t('content_master_filter_category_clear')}
                    title={t('content_master_filter_category_clear')}
                  >
                    ×
                  </button>
                </span>
              ) : wawiCategoryPickerOpen ? (
                <div className="content-master-filter-modal__category-picker">
                  <MarketplaceCategoryPicker
                    source={source}
                    dataSourceId={dataSourceId}
                    mode="select"
                    onSelect={handleWawiCategorySelect}
                    fetchPage={fetchWawiCategories}
                  />
                  <button
                    type="button"
                    className="content-master-filter-modal__category-cancel"
                    onClick={() => setWawiCategoryPickerOpen(false)}
                  >
                    {t('content_master_bulk_modal_cancel')}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="content-master-filter-modal__category-choose"
                  onClick={() => setWawiCategoryPickerOpen(true)}
                >
                  {t('content_master_filter_category_choose')}
                </button>
              )}
            </>
          ) : null}
          {anyCategorySelected ? (
            // ONE subtree toggle for both pickers (shared `includeSubcategories`).
            <label className="content-master-filter-modal__checkbox">
              <input
                type="checkbox"
                className="content-master-checkbox"
                checked={draftIncludeSubcategories}
                onChange={(e) => setDraftIncludeSubcategories(e.target.checked)}
              />
              <span>{t('content_master_filter_include_subcategories')}</span>
            </label>
          ) : null}
        </div>

        <div className="content-master-filter-modal__section">
          <span className="content-master-filter-modal__label">
            {t('content_master_filter_fields_label')}
          </span>
          {rows.map((row, index) => (
            <div className="content-master-filter-row" key={index}>
              <div className="content-master-filter-row__field">
                <button
                  type="button"
                  className={
                    row.field
                      ? 'content-master-filter-row__field-trigger'
                      : 'content-master-filter-row__field-trigger content-master-filter-row__field-trigger--empty'
                  }
                  onClick={() => toggleDropdown(index)}
                  aria-expanded={openRowIndex === index}
                >
                  {row.field
                    ? fieldLabel(row.field)
                    : t('content_master_filter_field_placeholder')}
                </button>
              </div>
              <input
                type="text"
                className="content-master-filter-row__value"
                value={row.value}
                maxLength={CONTENT_MASTER_FIELD_FILTER_VALUE_MAX}
                placeholder={t('content_master_filter_value_placeholder')}
                onChange={(e) => updateRowValue(index, e.target.value)}
              />
              <button
                type="button"
                className="content-master-filter-row__remove"
                onClick={() => removeRow(index)}
                aria-label={t('content_master_filter_row_remove')}
                title={t('content_master_filter_row_remove')}
              >
                ×
              </button>
              {openRowIndex === index ? (
                <div className="content-master-filter-row__dropdown" ref={dropdownRef}>
                  <input
                    type="text"
                    className="content-master-filter-row__dropdown-search"
                    value={fieldSearch}
                    onChange={(e) => setFieldSearch(e.target.value)}
                    placeholder={t('content_master_filter_field_search_placeholder')}
                    autoFocus
                  />
                  <div className="content-master-filter-row__dropdown-list">
                    {fieldsLoading ? (
                      <div className="content-master-filter-row__dropdown-hint">
                        {t('loading_text')}
                      </div>
                    ) : fieldsError ? (
                      <div className="content-master-filter-row__dropdown-hint">
                        {t('content_master_filter_fields_error')}
                      </div>
                    ) : groupsToRender.length === 0 ? (
                      <div className="content-master-filter-row__dropdown-hint">
                        {t('content_master_filter_no_fields')}
                      </div>
                    ) : (
                      groupsToRender.map((group) => (
                        <div
                          className="content-master-filter-row__dropdown-group"
                          key={group.key}
                        >
                          <div className="content-master-filter-row__dropdown-group-label">
                            {t(group.labelKey)}
                          </div>
                          {group.items.map((item) => (
                            <button
                              type="button"
                              key={item.field}
                              className="content-master-filter-row__dropdown-option"
                              onClick={() => selectField(index, item.field)}
                            >
                              {item.label}
                            </button>
                          ))}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          ))}
          {rows.length < CONTENT_MASTER_FIELD_FILTERS_MAX ? (
            <button
              type="button"
              className="content-master-filter-modal__add-row"
              onClick={addRow}
            >
              {t('content_master_filter_add_row')}
            </button>
          ) : (
            <div className="content-master-filter-modal__rows-limit">
              {t('content_master_filter_rows_limit', {
                max: CONTENT_MASTER_FIELD_FILTERS_MAX,
              })}
            </div>
          )}
        </div>
      </div>

      <div className="content-master-bulk-modal__buttons content-master-filter-modal__footer">
        <button
          type="button"
          className="content-master-bulk-modal__button content-master-bulk-modal__button--cancel"
          onClick={onClose}
        >
          {t('content_master_bulk_modal_cancel')}
        </button>
        <button
          type="button"
          className="content-master-bulk-modal__button content-master-bulk-modal__button--confirm"
          onClick={handleApply}
        >
          {t('content_master_filter_apply')}
        </button>
      </div>
    </Modal>
  );
};

export default ContentMasterFilterModal;
